// Core React Imports
import { useEffect, useRef, useCallback } from "react";

// Third-Party Libraries
import { useNavigate } from "react-router-dom";

// Local Imports
import ApiService from "../../data/ApiService";
import UtilsUnit from "../../utils/UtilsUnit";
import ConstantsPages from "../../constants/ConstantsPages";
import ConstantsTexts from "../../constants/ConstantsTexts";
import { useLoading } from "../../widgets/loadings/WidgetLoading";
import { useDispatch } from "react-redux";

export function usePageWelcome() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoading, hideLoading } = useLoading();

  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);

  // Fetches user data and filter data from API, sets them in Redux store, and navigates to filter page
  const fetchData = useCallback(
    async (code) => {
      showLoadingRef.current();

      try {
        const userData = await ApiService.getUserData(code);

        if (userData) {
          UtilsUnit.handleSetUserData(dispatch, userData.userData);
          UtilsUnit.handleSetTokens(userData.tokens);
          UtilsUnit.handleSetLoginTimestamp(dispatch);

          // Check if user has valid permissions before navigating
          if (UtilsUnit.hasValidUserTypeTerminal(userData.userData)) {
            navigate(ConstantsPages.PAGE_FILTER);
          } else {
            // User has insufficient permissions - show error and logout
            alert(ConstantsTexts.CODE_84);
            UtilsUnit.handleLogout(dispatch);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        hideLoadingRef.current();
      }
    },
    [dispatch, navigate]
  );

  // Handles OAuth authorization flow by checking for logout state, extracting code from URL, and initiating login or data fetch
  const handleAutorization = useCallback(() => {
    const logoutInProgress = UtilsUnit.getLogoutInProgress();

    // If this is a logout redirect, don't process the code
    if (logoutInProgress === "true") {
      UtilsUnit.handleRemoveLogoutInProgress();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    // If code is present, fetch data
    if (code) {
      fetchData(code);
    } else {
      UtilsUnit.login();
    }
  }, [fetchData]);

  // Fetches filter data for testing purposes
  // const fetchDataForTesting = useCallback(async () => {
  //   showLoadingRef.current();

  //   const userDataResponse = {
  //     ResponseMetadata: {
  //       RequestId: "ebb55492-763e-4a3d-9224-3e77588a2247",
  //       HTTPStatusCode: 200,
  //       HTTPHeaders: {},
  //       RetryAttempts: 0,
  //     },
  //     UserAttributes: [
  //       { Name: "email", Value: "EliorC@iroads.co.il" },
  //       { Name: "email_verified", Value: "true" },
  //       { Name: "family_name", Value: "Cohen" },
  //       { Name: "given_name", Value: "Elior" },
  //       { Name: "custom:UserTypePlanA", Value: "1" },
  //       { Name: "custom:UserTypeTerminal", Value: "3" },
  //       { Name: "sub", Value: "4a73921c-6011-70b6-b954-77485bda4031" },
  //     ],
  //     Username: "4a73921c-6011-70b6-b954-77485bda4031",
  //   };
  //   UtilsUnit.handleSetUserData(dispatch, userDataResponse);
  //   UtilsUnit.handleSetTokens({ access_token: "access_token", refresh_token: "refresh_token" });

  //   hideLoadingRef.current();

  //   // Check if user has valid permissions before navigating
  //   if (UtilsUnit.hasValidUserTypeTerminal(userDataResponse)) {
  //     navigate(ConstantsPages.PAGE_FILTER);
  //   } else {
  //     // User has insufficient permissions - show error and stay on welcome page
  //     alert(ConstantsTexts.CODE_84);
  //     UtilsUnit.handleLogout(dispatch);
  //   }
  // }, [dispatch, navigate]);

  // Initiate OAuth authorization flow
  useEffect(() => {
    // fetchDataForTesting(); // For testing
    handleAutorization();
    // }, [fetchDataForTesting]);
  }, [handleAutorization]);
}
